import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import type { AdminTrialItem } from '../types/adminTrial';
import { getDaysRemaining } from '../utils/trialHelpers';

interface TrialDaysBadgeProps {
  trial: AdminTrialItem;
  className?: string;
}

export function TrialDaysBadge({ trial, className }: TrialDaysBadgeProps) {
  const { t } = useTranslation();
  const days = trial.days_remaining ?? getDaysRemaining(trial.trial_ends_at);

  if (days === null) {
    return (
      <span className={cn('text-xs text-muted-foreground', className)}>
        {t('trials.daysRemaining.unavailable')}
      </span>
    );
  }

  if (
    days < 0 ||
    trial.subscription_status === 'expired' ||
    trial.subscription_status === 'cancelled'
  ) {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400',
          className,
        )}
      >
        {t('trials.daysRemaining.expired')}
      </span>
    );
  }

  if (days === 0) {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
          className,
        )}
      >
        {t('trials.daysRemaining.today')}
      </span>
    );
  }

  const isUrgent = days <= 3 && trial.subscription_status === 'trialing';

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        isUrgent
          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        className,
      )}
    >
      {t('trials.daysRemaining.left', { count: days })}
    </span>
  );
}
